import React, { Fragment, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { I18n, translate } from '@td/shared_utils';
import { Formik, Form, Field, FieldArray } from 'formik';
import { Switch } from '@td/td-ui-kit';
import { isEmpty, get } from 'lodash';
import classnames from 'classnames';

import { DrugListMap } from '../../types/drugListMap';
import { RefState } from '../../types/refState';
import { SERVICE_LINE_MANAGED_STATES_TRANSLATION_KEYS } from './medication-service-line-availability';
import StateAutocomplete from './state-autocomplete';
import Chip from '../../../components/chip';
import useMedicationAvailabilityEditForm from './use-medication-availability-edit-form';

const MedicationAvailabilityEditForm = ({ serviceLine, drugListMaps, refStates, onComplete, drugId }) => {
  const { initialValues, onSubmit, data, error } = useMedicationAvailabilityEditForm({
    serviceLine,
    drugListMaps,
    refStates,
    onComplete,
    drugId
  });

  const formRef = useRef();

  useEffect(() => {
    const eventListener = event => {
      if (formRef.current.dirty) {
        // event.preventDefault should be enough for the alert to be displayed but in some browsers it's still not supported
        // https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event
        event.preventDefault();
        event.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', eventListener);

    return () => {
      window.removeEventListener('beforeunload', eventListener);
    };
  }, [formRef.current]);

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit} innerRef={formRef}>
      {({ values: { managedStates, medicationAvailable }, isSubmitting, setFieldValue }) => (
        <Form className="medication-availability-edit-form">
          <h4>
            <I18n
              scope="medication_formularies.details.availability.edit_form"
              text="title"
              options={{
                service_line: translate(null, 'medication_formularies.details.availability.service_lines', serviceLine)
              }}
            />
          </h4>
          {(!isEmpty(error) || !isEmpty(get(data, ['createDrugListMaps', 'errors']))) && (
            <div className="alert-box">
              <I18n scope="medication_formularies.details.availability.edit_form" text="error" />
            </div>
          )}
          <div className="medication-available-switch">
            <label htmlFor={`medication-available-${serviceLine}`}>
              <I18n scope="medication_formularies.details.availability.edit_form.labels" text="medication_available" />
            </label>
            <Field id={`medication-available-${serviceLine}`} name="medicationAvailable">
              {({ field }) => (
                <Switch
                  {...field}
                  checked={field.value}
                  onChange={value => {
                    setFieldValue(field.name, value);

                    if (!value) {
                      setFieldValue('managedStates', []);
                    }
                  }}
                  pendingOnColor={'#42A6CB'}
                  offColor={'#dae1e7'}
                />
              )}
            </Field>
          </div>
          {medicationAvailable && (
            <div className="managed-states">
              <span className="managed-states-label">
                <I18n
                  scope="medication_formularies.details.availability.labels"
                  text={SERVICE_LINE_MANAGED_STATES_TRANSLATION_KEYS[serviceLine]}
                />
              </span>
              <FieldArray name="managedStates">
                {({ remove, push }) => (
                  <Fragment>
                    <ul>
                      {managedStates.map((managedState, refStateIndex) => (
                        <li key={managedState.stateName}>
                          <Chip label={managedState.stateName} onDelete={() => remove(refStateIndex)} />
                        </li>
                      ))}
                    </ul>
                    <StateAutocomplete onStateSelected={push} selectedStates={managedStates} refStates={refStates} />
                  </Fragment>
                )}
              </FieldArray>
            </div>
          )}
          <div className="form-actions">
            <button
              className={classnames(['button', 'button-secondary', { disabled: isSubmitting }])}
              onClick={onComplete}
              disabled={isSubmitting}
            >
              <I18n scope="medication_formularies.details.availability.edit_form.actions" text="cancel" />
            </button>
            <input
              className={classnames({ disabled: isSubmitting })}
              type="submit"
              value={translate(null, 'medication_formularies.details.availability.edit_form.actions', 'save')}
              disabled={isSubmitting}
            />
          </div>
        </Form>
      )}
    </Formik>
  );
};

MedicationAvailabilityEditForm.propTypes = {
  serviceLine: PropTypes.oneOf(['general_medical', 'mental_health', 'dermatology', 'primary_care']).isRequired,
  drugListMaps: PropTypes.arrayOf(DrugListMap).isRequired,
  refStates: PropTypes.arrayOf(RefState).isRequired,
  onComplete: PropTypes.func.isRequired,
  drugId: PropTypes.string.isRequired
};

export default MedicationAvailabilityEditForm;
